import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import DyanamicDropdown from "components/Elements/DyamicDropdown";
import ErrorInputFieldWrapper from "components/Layouts/ErrorInputFieldWrapper";
import { defaultPageSize } from "helpers/utils";
import {
	debounce,
	generateErrorState,
	handleSort,
	handleValidateObj,
} from "helpers/utils";
import React, { useCallback, useEffect, useState } from "react";
import {
	getClientUserSiteAppList,
	getClientUserSiteAppListCount,
} from "services/users/userDetails";
import AddDialogStyle from "styles/application/AddDialogStyle";
import * as yup from "yup";

const schema = yup.object({
	userID: yup
		.string("This field must be a string")
		.required("This field is required"),
});

const ADD = AddDialogStyle();
const defaultData = { userID: "" };
const defaultError = { userID: null };
const CONTACTS_PAGE_SIZE = 20;

const useStyles = makeStyles()((theme) => ({
	dialogContent: {
		display: "flex",
		flexDirection: "column",
	},
	createButton: {
		width: "auto",
	},
}));

const AddNoteDialog = ({ open, handleClose, createHandler, siteAppId }) => {
	const { classes, cx } = useStyles();
	const [input, setInput] = useState(defaultData);
	const [errors, setErrors] = useState(defaultError);
	const [isUpdating, setIsUpdating] = useState(false);
	const [page, setPage] = useState({ pageNo: 1, pageSize: CONTACTS_PAGE_SIZE });
	const [contactUsers, setContactUsers] = useState([]);
	const [count, setCount] = useState(0);
	const [currentTableSort, setCurrentTableSort] = useState(["firstName", "asc"]);
	const [searchText, setSearchText] = useState("");

	const closeOverride = () => {
		setInput(defaultData);
		setErrors(defaultError);
		setPage({ pageNo: 1, pageSize: CONTACTS_PAGE_SIZE });
		setContactUsers([]);
		setCount(0);
		setSearchText("");
		handleClose();
	};

	const handleCreateProcess = async () => {
		setIsUpdating(true);

		try {
			const localChecker = await handleValidateObj(schema, {
				userID: input?.userID?.id,
			});
			if (!localChecker.some((el) => el.valid === false)) {
				await createHandler(input.userID?.id);

				closeOverride();
			} else {
				const newErrors = generateErrorState(localChecker);
				setErrors({ ...errors, ...newErrors });
			}

			setIsUpdating(false);
		} catch (err) {
			setIsUpdating(false);
			closeOverride();
		}
	};

	const fetchClientUserSiteAppsCount = async (search) => {
		try {
			const response = await getClientUserSiteAppListCount({ 
				siteAppId, 
				search,
				sort: currentTableSort[1],
				sortField: currentTableSort[0],
				excludeKeyContacts: true
			});
			if (response.status) {
				setCount(response.data);
			}
		} catch (err) {
			console.error("Failed to fetch count", err);
		}
	};

	const fetchClientUserSiteApps = async (p, prevData) => {
		try {
			const pageNumber = parseInt(p);
			if (isNaN(pageNumber) || pageNumber < 1) return;

			const response = await getClientUserSiteAppList({
				siteAppId,
				pageNumber: pageNumber,
				pageSize: page.pageSize,
				search: searchText,
				sort: currentTableSort[1],
				sortField: currentTableSort[0],
				excludeKeyContacts: true
			});
			
			if (response.status) {
				if (pageNumber === 1) {
					setContactUsers([...response.data]);
				} else {
					setContactUsers([...prevData, ...response.data]);
				}
				return true;
			}
		} catch (err) {
			console.error("Failed to fetch users", err);
		}
		return false;
	};

	useEffect(() => {
		if (open) {
			Promise.all([
				fetchClientUserSiteApps(1, []),
				fetchClientUserSiteAppsCount(searchText),
			]);
		}
	}, [open, currentTableSort]);

	const pageChange = async (p, prevData) => {
		const pageNumber = parseInt(p);
		if (isNaN(pageNumber) || pageNumber < 1) return;
		
		await fetchClientUserSiteApps(pageNumber, prevData);
		setPage((prev) => ({ ...prev, pageNo: pageNumber }));
		return { data: contactUsers };
	};

	const handleServerSideSearch = useCallback(
		debounce(async (searchTxt) => {
			if (searchTxt !== searchText) {
				setPage({ pageNo: 1, pageSize: page.pageSize });
			}
			
			setSearchText(searchTxt);
			
			try {
				const response = await getClientUserSiteAppList({
					siteAppId,
					pageNumber: 1,
					pageSize: page.pageSize,
					search: searchTxt,
					sort: currentTableSort[1],
					sortField: currentTableSort[0],
					excludeKeyContacts: true
				});
				
				if (response.status) {
					setContactUsers(response.data);
					// Update count when search changes
					fetchClientUserSiteAppsCount(searchTxt);
				}
			} catch (err) {
				console.error("Search failed", err);
			}
		}, 500),
		[currentTableSort, searchText, page.pageSize, siteAppId]
	);

	const handleServerSideSort = useCallback((field, method) => {
		console.log(`Sorting by ${field} in ${method} order`);
		setCurrentTableSort([field, method]);
		setPage({ pageNo: 1, pageSize: page.pageSize });
		setContactUsers([]);
	}, []);

	return (
		<Dialog
			open={open}
			onClose={closeOverride}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			className="application-dailog"
		>
			{isUpdating ? <LinearProgress /> : null}
			<ADD.ActionContainer>
				<DialogTitle id="alert-dialog-title">
					{<ADD.HeaderText>Add Key Contact</ADD.HeaderText>}
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton
						onClick={closeOverride}
						variant="contained"
						style={{ width: "auto" }}
					>
						Cancel
					</ADD.CancelButton>
					<ADD.ConfirmButton
						onClick={handleCreateProcess}
						variant="contained"
						className={classes.createButton}
						disabled={isUpdating}
					>
						Save
					</ADD.ConfirmButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<DialogContent className={classes.dialogContent}>
				<ErrorInputFieldWrapper
					errorMessage={errors.userID === null ? null : errors.userID}
				>
					<DyanamicDropdown
						dataSource={contactUsers}
						isServerSide={true}
						width="100%"
						placeholder="Select User"
						dataHeader={[
							{ id: 1, name: "FirstName" },
							{ id: 2, name: "LastName" },
							{ id: 3, name: "Email" },
						]}
						columns={[
							{ id: 1, name: "firstName" },
							{ id: 2, name: "lastName" },
							{ id: 3, name: "email" },
						]}
						selectedValue={{
							...input["userID"],
							firstName: !input?.userID?.firstName
								? ""
								: !input?.userID?.lastName
								? input?.userID?.firstName
								: input?.userID?.firstName + " " + input?.userID?.lastName,
						}}
						handleSort={handleSort}
						handleServerSideSearch={handleServerSideSearch}
						handleServerSideSort={handleServerSideSort}
						onChange={(val) => {
							setInput({ ...input, userID: val });
						}}
						selectdValueToshow="firstName"
						label={"Attach User"}
						required
						isError={errors.userID === null ? false : true}
						onPageChange={pageChange}
						page={page}
						count={count}
						showHeader
						dropDownActiveWidth={"580px"}
					/>
				</ErrorInputFieldWrapper>
			</DialogContent>
		</Dialog>
	);
};

export default AddNoteDialog;
